import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onMouseEnter", "onMouseLeave", "onFocus", "onBlur", "enterDelay", "visualOnly"],
  _excluded2 = ["aria-owns"];
import { useFlyout } from "../flyout";
import { useDelayedOpen } from "../flyout/hooks/useDelayedOpen";
import { useFocusMode } from "../focusMode";
import { tooltipFlyoutDefaults } from "./tooltipConstants";
/**
 * Provides a convenience wrapper over the `useFlyout` hook with reasonable
 * defaults for a Tooltip.
 *
 * If `isOpen` is provided, we'll use it. Otherwise, set up some functionality to manage it internally.
 * This is an example of controlled/uncontrolled behavior that usually we try and stay away
 * from in lodestar-core, but Tooltip is an example of where the most common use-case will be
 * uncontrolled. We still want to provide our users with an escape hatch.
 */
export var useTooltip = function useTooltip() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    onMouseEnter = _ref.onMouseEnter,
    onMouseLeave = _ref.onMouseLeave,
    providedOnFocus = _ref.onFocus,
    onBlur = _ref.onBlur,
    _ref$enterDelay = _ref.enterDelay,
    enterDelay = _ref$enterDelay === void 0 ? 500 : _ref$enterDelay,
    _ref$visualOnly = _ref.visualOnly,
    visualOnly = _ref$visualOnly === void 0 ? false : _ref$visualOnly,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useDelayedOpen = useDelayedOpen({
      enterDelay: enterDelay
    }),
    isOpen = _useDelayedOpen.isOpen,
    setIsOpen = _useDelayedOpen.setIsOpen,
    cancelEnterTimer = _useDelayedOpen.cancelEnterTimer,
    getDelayedOpenTargetProps = _useDelayedOpen.getDelayedOpenTargetProps;

  // When the user is in mouse mode, we have a special case here. When focus moves to the
  // tooltip's target we don't want to show the Tooltip because there's no indication that you're
  // focused on anything (no outline). It's awkward to see the tooltip pop up out of nowhere.
  var _useFocusMode = useFocusMode({
      passive: true
    }),
    getFocusMode = _useFocusMode.getFocusMode;
  var onFocus = function onFocus(e) {
    if (getFocusMode() === "mouse") {
      // Close the Tooltip if it's open already.
      setIsOpen(false);
      // After any timers are set by useDelayedOpen, cancel them so the Tooltip doesn't reopen
      // after the ususal timer started in useDelayedOpen.onFocus().
      requestAnimationFrame(function () {
        cancelEnterTimer();
      });
    }
    providedOnFocus === null || providedOnFocus === void 0 ? void 0 : providedOnFocus(e);
  };
  var _useFlyout = useFlyout(_objectSpread(_objectSpread(_objectSpread({}, tooltipFlyoutDefaults), rest), {}, {
      isOpen: isOpen
    })),
    flyoutProps = _useFlyout.flyoutProps,
    _useFlyout$targetProp = _useFlyout.targetProps,
    _0 = _useFlyout$targetProp["aria-owns"],
    restTargetProps = _objectWithoutProperties(_useFlyout$targetProp, _excluded2);
  return {
    tooltipProps: flyoutProps,
    targetProps: _objectSpread(_objectSpread(_objectSpread({}, restTargetProps), flyoutProps.isOpen && !visualOnly ? {
      "aria-describedby": flyoutProps.id
    } : {}), getDelayedOpenTargetProps({
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      onFocus: onFocus,
      onBlur: onBlur
    }))
  };
};
//# sourceMappingURL=useTooltip.js.map